// src/types/Venue.ts
import { Timestamp } from 'firebase/firestore';

export type VenueType = 'restaurant' | 'event_space' | 'resort';

export interface BusinessHours {
  dayOfWeek: number; // 0-6, where 0 is Sunday
  open: string; // "09:00"
  close: string; // "17:00"
  isClosed: boolean;
}

export interface VenueLocation {
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

export interface VenueContact {
  email: string;
  phone: string;
  website?: string;
}

export interface VenueSocialMedia {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
}

export interface VenueImages {
  primary: string; // URL to main venue image
  gallery: string[]; // URLs to additional venue images
  logo: string; // URL to venue logo
}

export interface Venue {
  id: string;
  name: string;
  type: VenueType;
  description: string;
  contact: VenueContact;
  socialMedia?: VenueSocialMedia;
  location: VenueLocation;
  logoUrl?: string; // Added for use in the account switcher
  amenities: string[];
  businessHours: BusinessHours[];
  images: VenueImages;
  packages: string[]; // References to package IDs
  ownerId: string; // User ID of the owner
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface VenueFormData {
  name: string;
  type: VenueType;
  description: string;
  contact: VenueContact;
  socialMedia?: VenueSocialMedia;
  location: VenueLocation;
  amenities: string[];
  businessHours: BusinessHours[];
  images?: Partial<VenueImages>;
}

export type PackageType = 'dinner' | 'experience' | 'event' | 'special';

// Package type constants for easier reference
export const PackageType = {
  Dinner: 'dinner' as PackageType,
  Experience: 'experience' as PackageType,
  Event: 'event' as PackageType,
  Special: 'special' as PackageType
};

export interface Package {
  id: string;
  venueId: string;
  name: string;
  description: string;
  packageType: PackageType;
  price: number;
  discountedPrice?: number;
  images: string[];
  includesItems: string[];
  availability: {
    startDate?: Date | null;
    endDate?: Date | null;
    daysAvailable: number[]; // 0-6 for days of week
    timeSlots: {
      start: string; // "18:00"
      end: string; // "20:00"
    }[];
  };
  maxCapacity: number;
  restrictions?: string[];
  isActive: boolean;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface PackageFormData {
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  images?: string[];
  includesItems: string[];
  availability: {
    startDate?: Date;
    endDate?: Date;
    daysAvailable?: number[];
    timeSlots?: {
      start: string;
      end: string;
    }[];
  };
  maxCapacity?: number;
  isActive: boolean;
}
